import { useParams } from "react-router-dom";

import { EmptyState, LoadingState } from "~/common/components";
import {
  useGetPatient,
  useGetReimbursementReport,
  useIsTeamMemberStaff,
} from "~/providers/hooks";
import {
  isConsultationCompleted,
  isZoomFormPending,
  PatientParams,
  showZoomMeetingForm,
} from "~/providers/utils";
import { DisabledReimbursementReportMessage } from "./DisabledReimbursementReportMessage";
import { ReimbursementReportCreateForm } from "./ReimbursementReportCreateForm";
import { ReimbursementReportForm } from "./ReimbursementReportForm";
import { SubmittedReimbursementReport } from "./SubmittedReimbursementReport";

export const ReimbursementReport = () => {
  const { patientId } = useParams<keyof PatientParams>() as PatientParams;
  const { data: patientData } = useGetPatient(patientId);
  const { isStaff } = useIsTeamMemberStaff();
  const consultationId = patientData?.consultation?.id;
  const consultationStatus = patientData?.consultation?.status;
  const isConsultationComplete = isConsultationCompleted(consultationStatus);

  const { data: reportData, isLoading } = useGetReimbursementReport(
    consultationId,
    {
      enabled: isConsultationComplete,
      refetchInterval: (query) => {
        const report = query.state.data;
        return report && isZoomFormPending(report) ? 30_000 : false;
      },
    },
  );

  if (!patientData) {
    return null;
  }

  if (isLoading) return <LoadingState className="h-screen-calculated" />;

  if (reportData?.completed_at) {
    return <SubmittedReimbursementReport data={reportData} />;
  }

  if (isStaff) {
    return (
      <EmptyState
        message="No progress note available for this patient."
        messageClassName="lg:w-1/4"
      />
    );
  }

  if (!isConsultationComplete) {
    return <DisabledReimbursementReportMessage />;
  }

  if (!reportData || showZoomMeetingForm(reportData)) {
    return (
      <ReimbursementReportCreateForm reportData={reportData ?? undefined} />
    );
  }

  return <ReimbursementReportForm data={reportData} />;
};
